import React, { useEffect, useRef, useState } from 'react';
import { Shield, Smartphone, Mail, AlertCircle, CheckCircle, Loader, ArrowLeft, Download, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiPost } from '../../utils/api';
import { getToken, setToken, clearToken } from '../../stores/authStore';

/**
 * Forced 2FA enrollment shown at login when the org's enforcement policy
 * requires 2FA but this user hasn't set it up yet. Reuses the same
 * /two_factor/setup + /two_factor/enable endpoints as SecuritySettings.
 *
 * Auth approach: the parent passes the `setupToken` issued by the auth
 * controller. We stash it as the active auth token (so apiPost picks it up)
 * for the duration of this component; the backend pins it to /two_factor/*
 * endpoints only, so it can't be misused. On success or back-out, we clear
 * the token and force the user back to login with their now-enabled 2FA.
 */
const ForceSetupTwoFactor = ({ setupToken, user, onBack, onComplete }) => {
  const navigate = useNavigate();
  const [method, setMethod] = useState(null);             // 'totp' | 'email'
  const [stage, setStage] = useState('choose');           // choose | totp_qr | enter_code | success
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [otpSecret, setOtpSecret] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Stash setup token so apiPost uses it for /two_factor/* calls. We save the
  // previous token (if any) and restore it on unmount instead of unconditionally
  // clearing — guards against edge cases where this component is mounted while
  // another auth flow has set a token (multi-account, dev tools, etc.).
  const tokenSetRef = useRef(false);
  const previousTokenRef = useRef(null);
  useEffect(() => {
    if (setupToken && !tokenSetRef.current) {
      previousTokenRef.current = getToken();
      setToken(setupToken);
      tokenSetRef.current = true;
    }
    return () => {
      if (tokenSetRef.current) {
        // Restore the previous token if there was one, otherwise just clear.
        if (previousTokenRef.current) {
          setToken(previousTokenRef.current);
        } else {
          clearToken();
        }
        previousTokenRef.current = null;
        tokenSetRef.current = false;
      }
    };
  }, [setupToken]);

  const handleChooseMethod = async (chosen) => {
    setError('');
    setMethod(chosen);
    setLoading(true);
    try {
      const resp = await apiPost('/two_factor/setup', { method: chosen });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Setup failed');

      if (chosen === 'totp') {
        // Accept any of the QR field names the backend has returned over time.
        // SecuritySettings checks `data.qr_code`; older shapes use qr_code_url
        // or provisioning_uri. Cover all three to avoid a blank QR render.
        setQrCodeUrl(data.qr_code_url || data.qr_code || data.provisioning_uri || '');
        setOtpSecret(data.secret || '');
        setStage('totp_qr');
      } else if (chosen === 'email') {
        // Email OTP was sent to the user's email
        setStage('enter_code');
      }
    } catch (e) {
      setError(e.message);
      setMethod(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    setError('');
    if (!otpCode.trim()) {
      setError('Enter the verification code');
      return;
    }
    setLoading(true);
    try {
      const resp = await apiPost('/two_factor/enable', { method, otp_code: otpCode.trim() });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Verification failed');

      setBackupCodes(Array.isArray(data.backup_codes) ? data.backup_codes : []);
      setStage('success');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    // Setup token is now obsolete — drop it. Also clear the saved-previous
    // token reference so the unmount cleanup doesn't restore a stale value;
    // the user re-logs in to get a fresh access token with their new 2FA enabled.
    clearToken();
    tokenSetRef.current = false;
    previousTokenRef.current = null;
    // Notify the parent so it can reset its state machine back to the login form.
    // navigate('/login') alone wouldn't work because we're already at /login —
    // React Router doesn't re-render the route and the parent's setup-required
    // flag is still true, so the wizard would keep rendering.
    if (onComplete) {
      onComplete();
    } else {
      navigate('/login', { replace: true });
    }
  };

  // Build a human-readable text payload with the backup codes, identifying
  // info, and a usage reminder. Triggers a download as <account>-backup-codes.txt.
  const handleDownloadBackupCodes = () => {
    if (backupCodes.length === 0) return;
    const generatedAt = new Date().toLocaleString();
    const identifier = (user?.email || user?.username || 'jpro-account').toString();
    const lines = [
      'JPRO — Two-factor authentication backup codes',
      '',
      `Account:    ${identifier}`,
      `Generated:  ${generatedAt}`,
      '',
      'Each code can be used ONCE if you lose access to your authenticator',
      'method. Store them somewhere safe (password manager, printed copy, etc.).',
      '',
      '-----  BACKUP CODES  -----',
      ...backupCodes,
      '--------------------------',
      '',
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Safe filename: strip anything that's not alphanumeric / dash / underscore / dot
    const safeId = identifier.replace(/[^a-zA-Z0-9._-]/g, '_');
    a.download = `${safeId}-2fa-backup-codes.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const [codesCopied, setCodesCopied] = useState(false);
  const handleCopyBackupCodes = async () => {
    if (backupCodes.length === 0) return;
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      setCodesCopied(true);
      window.setTimeout(() => setCodesCopied(false), 2000);
    } catch (e) {
      // Clipboard API can be denied (insecure context, permission). Fall back to selecting the codes
      // text — user can ctrl+C. We don't surface an error; the download button is the primary path.
      console.warn('Clipboard copy failed:', e);
    }
  };

  return (
    <div className="w-full max-w-lg mx-auto p-6 bg-white dark:bg-gray-800 rounded-xl shadow-md">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex-shrink-0">
          <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold font-rasa text-gray-900 dark:text-white">
            Two-factor authentication required
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Your administrator requires 2FA for accounts like yours. Set it up to continue.
          </p>
          {user?.email && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Signed in as <span className="font-mono">{user.email}</span>
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 p-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* Method picker */}
      {stage === 'choose' && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Choose a method:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => handleChooseMethod('totp')}
              disabled={loading}
              className="flex flex-col items-center p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors disabled:opacity-50"
            >
              <Smartphone className="h-8 w-8 text-blue-500 mb-2" />
              <span className="font-medium text-gray-900 dark:text-white">Authenticator App</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                Google Authenticator, Authy, 1Password
              </span>
            </button>

            <button
              onClick={() => handleChooseMethod('email')}
              disabled={loading}
              className="flex flex-col items-center p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors disabled:opacity-50"
            >
              <Mail className="h-8 w-8 text-orange-500 mb-2" />
              <span className="font-medium text-gray-900 dark:text-white">Email</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                Codes sent to your inbox
              </span>
            </button>
          </div>
        </div>
      )}

      {/* TOTP QR display */}
      {stage === 'totp_qr' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-200">
            Scan this QR code with your authenticator app, then enter the 6-digit code it shows.
          </p>
          {qrCodeUrl && (
            <div className="flex justify-center bg-white p-4 rounded-lg border border-gray-200">
              <img src={qrCodeUrl} alt="2FA QR code" className="w-44 h-44" />
            </div>
          )}
          {otpSecret && (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              Or enter the secret manually: <span className="font-mono text-gray-700 dark:text-gray-200">{otpSecret}</span>
            </p>
          )}
          <button
            onClick={() => setStage('enter_code')}
            className="w-full py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            I've added it — enter code
          </button>
        </div>
      )}

      {/* Code entry */}
      {stage === 'enter_code' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-200">
            {method === 'totp'
              ? 'Enter the 6-digit code from your authenticator app.'
              : 'Enter the 6-digit code we just emailed you.'}
          </p>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={6}
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            autoFocus
            className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex gap-2">
            <button
              onClick={() => { setOtpCode(''); setError(''); setStage('choose'); setMethod(null); }}
              disabled={loading}
              aria-label="Back to method selection"
              title="Back to method selection"
              className="flex-shrink-0 px-3 py-2 text-sm rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              onClick={handleEnable}
              disabled={loading || otpCode.length !== 6}
              className="flex-1 inline-flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : null}
              Verify and enable
            </button>
          </div>
        </div>
      )}

      {/* Success + backup codes */}
      {stage === 'success' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium font-rasa">2FA enabled</span>
          </div>
          {backupCodes.length > 0 && (
            <div className="p-3 rounded-md bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/40">
              <p className="text-sm font-medium text-orange-900 dark:text-orange-200">
                Save these backup codes — you'll need one if you lose access to your authenticator:
              </p>
              <div className="grid grid-cols-2 gap-1 mt-2">
                {backupCodes.map((code) => (
                  <div key={code} className="font-mono text-sm text-gray-900 dark:text-white px-2 py-1 bg-white dark:bg-gray-800 rounded border border-orange-200 dark:border-orange-800/40">
                    {code}
                  </div>
                ))}
              </div>

              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={handleDownloadBackupCodes}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download as .txt
                </button>
                <button
                  type="button"
                  onClick={handleCopyBackupCodes}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {codesCopied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {codesCopied ? 'Copied' : 'Copy all'}
                </button>
              </div>

              <p className="text-xs text-orange-800 dark:text-orange-300 mt-2">
                Each code can be used once. Store them somewhere safe.
              </p>
            </div>
          )}
          <button
            onClick={handleFinish}
            className="w-full py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            Continue to login
          </button>
        </div>
      )}

      {/* Bail-out option — back to login. Setup token gets cleared on unmount. */}
      {stage !== 'success' && onBack && (
        <div className="mt-6 text-center">
          <button onClick={onBack} className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            Cancel and return to login
          </button>
        </div>
      )}
    </div>
  );
};

export default ForceSetupTwoFactor;
